import { cn } from '@core/lib/cn';
import type { PriceBreakdownOut, PriceTotalsOut } from '@core/lib/api/types';
import { formatPaise } from '@core/lib/format';

export interface PriceBreakdownProps {
  breakdown: PriceBreakdownOut;
  totals: PriceTotalsOut;
  /** Open by default on the last step, where the customer is about to pay. */
  defaultOpen?: boolean;
  className?: string;
}

/**
 * The itemised price, behind a disclosure.
 *
 * `<details>`/`<summary>` rather than a hand-rolled toggle: it is open/closed
 * without JavaScript, it is a real keyboard control with the platform's own
 * expanded state, and Ctrl+F on desktop opens it to reveal a match. A div with
 * an `onClick` and an `aria-expanded` is more code and less function.
 *
 * EVERY AMOUNT IS FORMATTED FROM `*_paise`. The API also sends pre-formatted
 * strings (`amount`, `total_display`) and we deliberately ignore them for
 * display: one formatter means the breakdown lines and the sticky total can
 * never disagree about grouping or the rupee symbol. The integers are the
 * contract; the strings are a convenience for logs.
 */
export function PriceBreakdown({
  breakdown,
  totals,
  defaultOpen = false,
  className,
}: PriceBreakdownProps) {
  // The server says so rather than the client guessing from a zero: a real
  // ₹0 delivery exists (it is waived over the free-shipping threshold), and
  // telling the two apart by the amount would hide a genuine freebie behind
  // "not worked out yet".
  const shipping = (breakdown.meta?.['shipping'] ?? {}) as { status?: string };
  const deliveryPending = shipping.status === 'pending_pincode';

  return (
    <details
      className={cn('group rounded-card border border-line bg-surface', className)}
      open={defaultOpen}
    >
      <summary className="flex min-h-touch cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2">
        <span>Price breakdown</span>
        <span className="flex items-center gap-2 text-ink-muted">
          <span className="tabular-nums">{formatPaise(totals.total_paise)}</span>
          <span aria-hidden="true" className="transition-transform group-open:rotate-180">
            ⌄
          </span>
        </span>
      </summary>

      <div className="border-t border-line px-4 py-3">
        <dl>
          {breakdown.lines.map((line, index) => {
            // A delivery charge we have not worked out yet is NOT zero, and
            // rendering it as ₹0.00 reads as free delivery — then the price
            // goes up at the address step and the shop looks like it moved
            // the goalposts. An em dash says "no answer yet", which is the
            // truth.
            const unknown = line.code === 'shipping' && deliveryPending;
            return (
              // Code AND position: `code` is not unique. A two-document
              // order has a "printing" line per document, and keying on the
              // code alone makes React treat them as one row — it renders
              // one and drops the other, so a customer sees a total that
              // does not match the lines above it.
              <div
                key={`${line.code}-${index}`}
                className="flex items-baseline justify-between gap-4 py-1.5"
              >
                <dt className="min-w-0 text-sm text-ink-muted">
                  {line.label}
                  {line.note ? (
                    <span className="block text-xs text-ink-subtle">{line.note}</span>
                  ) : null}
                </dt>
                <dd
                  className={cn(
                    'shrink-0 text-right text-sm font-medium tabular-nums',
                    line.kind === 'credit' ? 'text-success' : 'text-ink',
                  )}
                >
                  {/* A credit arrives ALREADY NEGATIVE — the rows have to sum to
                    the total, so the engine cannot send it any other way. The
                    minus we draw is a typographic one (U+2212, which lines up
                    with the digits); the arithmetic sign is stripped first, or
                    the two stack up and a ten percent discount reads back as
                    "−-₹1.50". A discount that renders wrong is the kind of bug
                    a customer notices before we do. */}
                  {unknown ? (
                    <span aria-label="not worked out yet">—</span>
                  ) : (
                    <>
                      {line.kind === 'credit' ? '−' : ''}
                      {formatPaise(
                        line.kind === 'credit' ? Math.abs(line.amount_paise) : line.amount_paise,
                      )}
                    </>
                  )}
                </dd>
              </div>
            );
          })}

          <div className="mt-2 flex items-baseline justify-between gap-4 border-t border-line pt-3">
            <dt className="text-sm font-semibold text-ink">
              Total
              {deliveryPending ? (
                <span className="block text-xs font-normal text-ink-subtle">Before delivery</span>
              ) : null}
            </dt>
            <dd className="text-right text-lg font-semibold tabular-nums text-ink">
              {formatPaise(totals.total_paise)}
            </dd>
          </div>
        </dl>
      </div>
    </details>
  );
}
